/**
 * Lista agendas PA ativas da organização (visão clínica).
 * Só médicos disponíveis e dentro da jornada de hoje (+ tolerância).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { oids } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";
import { GetSchedule } from "@/services/fhir";
import { isClinicProntoScheduleWithinHours } from "./filterAvailableProntoSchedules";
import {
  extractHealthcareServicesFromBundle,
  fetchHealthcareServiceById,
  getHealthcareServiceIdFromSchedule,
  PRONTO_ATENDIMENTO_INTERNAL_TOLERANCE_MINUTES,
} from "./isProntoHealthcareServiceAvailableNow";
import {
  extractSchedulesAndLocations,
  isProntoAtendimentoSchedule,
  mapScheduleToConfig,
} from "./mapScheduleToConfig";
import { PRONTO_ATENDIMENTO_SERVICE_CATEGORY_TOKEN } from "./serviceCategory";
import { useProntoAtendimentoNow } from "./useProntoAtendimentoNow";
import type { ProntoAtendimentoConfig } from "./types";

type ClinicDoctorRow = {
  schedule: fhir5.Schedule;
  hcs: fhir5.HealthcareService | undefined;
};

export function useProntoAtendimentoClinicDoctors(options?: {
  enabled?: boolean;
}) {
  const enabled = options?.enabled !== false;
  const { access } = useSessionContext();
  const now = useProntoAtendimentoNow();
  const [rows, setRows] = useState<ClinicDoctorRow[]>([]);
  const [locationsById, setLocationsById] = useState<
    Map<string, fhir5.Location>
  >(new Map());
  const [loading, setLoading] = useState(false);

  const organizationToken = access?.organizationId
    ? `${oids.cnpj}-${access.organizationId}`
    : undefined;

  const load = useCallback(async () => {
    if (!organizationToken) return;
    setLoading(true);
    try {
      const bundle = await GetSchedule({
        organization: organizationToken,
        includeLocation: true,
        includeHealthcareService: true,
        serviceCategory: PRONTO_ATENDIMENTO_SERVICE_CATEGORY_TOKEN,
        active: true,
      });

      const extracted = extractSchedulesAndLocations(bundle);
      const healthcareServicesById = extractHealthcareServicesFromBundle(bundle);
      const prontoSchedules = extracted.schedules.filter(
        isProntoAtendimentoSchedule,
      );

      const resolved = await Promise.all(
        prontoSchedules.map(async (schedule) => {
          const hcsId = getHealthcareServiceIdFromSchedule(schedule);
          if (!hcsId) return null;
          const hcs =
            healthcareServicesById.get(hcsId) ??
            (await fetchHealthcareServiceById(hcsId));
          return { schedule, hcs };
        }),
      );

      setLocationsById(extracted.locationsById);
      setRows(
        resolved.filter((row): row is ClinicDoctorRow => Boolean(row)),
      );
    } catch (error) {
      console.error("useProntoAtendimentoClinicDoctors", error);
      toast.error("Não foi possível carregar os médicos disponíveis.");
      setRows([]);
      setLocationsById(new Map());
    } finally {
      setLoading(false);
    }
  }, [organizationToken]);

  const doctors = useMemo(() => {
    return rows
      .filter(({ schedule, hcs }) =>
        isClinicProntoScheduleWithinHours(
          schedule,
          hcs,
          PRONTO_ATENDIMENTO_INTERNAL_TOLERANCE_MINUTES,
          now,
        ),
      )
      .map(({ schedule }) => mapScheduleToConfig(schedule, locationsById))
      .filter((c): c is ProntoAtendimentoConfig => Boolean(c?.practitionerRef));
  }, [rows, locationsById, now]);

  useEffect(() => {
    if (!enabled) return;
    void load();
  }, [enabled, load]);

  return { doctors, loading, refetch: load };
}
